/**
 * Shared table styles for consistent table appearance across the application
 * Use these constants to ensure visual consistency in all table components
 */

// Table container styles
export const tableContainerStyles = {
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  borderRadius: 2,
  '& .MuiPaper-root': {
    boxShadow: 'none',
  },
};

// Table header row styles
export const tableHeadRowStyles = {
  '& th': {
    bgcolor: '#fafafa',
    borderBottom: '2px solid #dee2e6',
    fontWeight: 600,
    fontSize: '13px',
    color: '#333',
    py: 1.5,
    whiteSpace: 'nowrap',
  },
};

// Table body row styles
export const tableBodyRowStyles = {
  cursor: 'pointer',
  '& td': {
    borderBottom: '1px solid #eee',
    py: 1.2,
    fontSize: '13px',
  },
  '&:hover': {
    backgroundColor: '#f8f9fa',
  },
};

// Table pagination styles
export const tablePaginationStyles = {
  borderTop: '1px solid #dee2e6',
  '& .MuiTablePagination-toolbar': {
    minHeight: 48,
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: '13px',
  },
};

// Empty state container styles
export const emptyStateStyles = {
  py: 4,
  textAlign: 'center',
};

// Empty state text styles
export const emptyStateTextStyles = {
  fontSize: '14px',
  color: 'text.secondary',
};

// Link button in empty state
export const emptyStateLinkStyles = {
  textTransform: 'none',
  color: '#875A7B',
};

// Primary link/name styles (for clickable names in tables)
export const primaryLinkStyles = {
  fontWeight: 500,
  fontSize: '13px',
  color: '#875A7B',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
};

// Secondary text styles (for subtitles in table cells)
export const secondaryTextStyles = {
  fontSize: '12px',
  color: '#666',
};

// Standard text styles
export const standardTextStyles = {
  fontSize: '13px',
  color: '#333',
};

// Avatar styles for table cells
export const tableAvatarStyles = {
  width: 28,
  height: 28,
  bgcolor: '#875A7B',
  fontSize: '11px',
  fontWeight: 600,
};

// Loading row styles
export const loadingRowStyles = {
  py: 4,
  textAlign: 'center',
};

// Action icon button styles
export const actionIconStyles = {
  fontSize: 18,
};

// Chip styles for status
export const statusChipStyles = {
  height: 24,
  fontSize: '12px',
  fontWeight: 500,
  borderRadius: '4px',
};

// Outlined chip styles
export const outlinedChipStyles = {
  height: 24,
  fontSize: '12px',
  borderRadius: '4px',
};

// Hidden columns for responsive tables
export const responsiveHiddenCell = {
  xs: { display: { xs: 'none', sm: 'table-cell' } },
  sm: { display: { xs: 'none', md: 'table-cell' } },
  md: { display: { xs: 'none', lg: 'table-cell' } },
};

/**
 * Combine table styles into a complete configuration object
 * @param {object} overrides - Style overrides
 * @returns {object} Complete table styles
 */
export const getTableStyles = (overrides = {}) => ({
  container: { ...tableContainerStyles, ...overrides.container },
  headRow: { ...tableHeadRowStyles, ...overrides.headRow },
  bodyRow: { ...tableBodyRowStyles, ...overrides.bodyRow },
  pagination: { ...tablePaginationStyles, ...overrides.pagination },
  emptyState: { ...emptyStateStyles, ...overrides.emptyState },
});

export default {
  tableContainerStyles,
  tableHeadRowStyles,
  tableBodyRowStyles,
  tablePaginationStyles,
  emptyStateStyles,
  emptyStateTextStyles,
  emptyStateLinkStyles,
  primaryLinkStyles,
  secondaryTextStyles,
  standardTextStyles,
  tableAvatarStyles,
  loadingRowStyles,
  actionIconStyles,
  statusChipStyles,
  outlinedChipStyles,
  responsiveHiddenCell,
  getTableStyles,
};
